import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Icon3D from '../Icons3D';
import { setDrawerOpen } from '../../state/uiSlice';
import { useDismissable } from '../../hooks';
import { useCopilot } from '../../api/copilot';
import { useProfile } from '../../api/hooks';
import { SCREENS } from '../../lib/agent/discovery';
import { screenForPath } from '../../lib/agent/screens';
import {
  AgentStatusDot, CopilotComposer, CopilotThread, SpeakingIndicator,
} from '../copilot';
import { callSafely, offersReconnect, restingText } from '../copilot/util';

/**
 * Starters that work on any screen and for any seat. They used to be the demo
 * persona's own to-do list ("the Sharma rebalance"), which read as broken to
 * anyone signed in as themselves.
 */
const SUGGESTIONS = [
  'What am I looking at?',
  'Summarize what needs my attention today',
  'What are the biggest risks on this screen?',
  'Open my watchlist',
];

/**
 * Right-hand Copilot drawer — context-aware Q&A over the screen in view.
 *
 * The same conversation as the dock, in an overlay: the thread, the cards and
 * the composer are the shared `components/copilot` set reading one provider,
 * so a question asked in one shows up in the other.
 *
 * `inert` and `aria-hidden` while closed, like the dock: the drawer is parked
 * off-screen rather than unmounted (so it can slide), and without them a
 * keyboard could tab into it and a screen reader would read a panel nobody can
 * see.
 */
export default function CopilotDrawer() {
  const dispatch = useDispatch();
  const drawerOpen = useSelector((s) => s.ui.drawerOpen);
  const isLoggedIn = useSelector((s) => s.auth?.isLoggedIn);
  const { pathname } = useLocation();
  const { user } = useProfile();
  const {
    connection = 'idle', reconnect, stalled = false,
  } = useCopilot();
  const drawerRef = useRef(null);
  const close = useCallback(() => dispatch(setDrawerOpen(false)), [dispatch]);
  useDismissable(drawerOpen, drawerRef, close);

  /* The signed-in account's own name, never a seat's demo persona: greeting a
     real user as someone else is worse than greeting them by no name. */
  const firstName = (isLoggedIn ? String(user?.name ?? '') : '').trim().split(/\s+/)[0] || '';
  /* What the agent is told it is looking at, named the way the catalogue names
     it ("FundDeepDive" read as "Fund Deep Dive"), so the header and the agent
     agree about "this screen". */
  const screenTitle = (SCREENS[screenForPath(pathname)]?.screen || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2');

  const ready = `Hi${firstName ? ` ${firstName}` : ''} — ask me about this screen, any client, fund or portfolio. ` +
    'Type, press the mic to talk, or pick a starter below.';

  return (
    <>
      <div
        className={drawerOpen ? 'scrim on' : 'scrim'}
        onClick={close}
      />
      <aside
        className={drawerOpen ? 'drawer open' : 'drawer'}
        ref={drawerRef}
        aria-label="KeepInvesting assistant"
        aria-hidden={!drawerOpen}
        inert={!drawerOpen ? '' : undefined}
      >
        <div className="drawer-h">
          <div className="ai-orb ic3fi"><Icon3D name="spark" /></div>
          <div className="cp-drawer-id">
            <b>KeepInvesting</b>
            <span className="cp-drawer-sub">
              <AgentStatusDot />
              {screenTitle && <span className="cp-drawer-ctx">{screenTitle}</span>}
            </span>
          </div>
          <span className="cp-head-tools">
            <SpeakingIndicator />
            {offersReconnect(connection, stalled) && (
              <button type="button" className="cp-link-btn" onClick={() => callSafely(reconnect)}>
                Reconnect
              </button>
            )}
          </span>
          <button type="button" className="drawer-x" onClick={close} title="Close assistant" aria-label="Close assistant">✕</button>
        </div>

        <CopilotThread variant="drawer" greeting={restingText(connection, ready)}>
          <div className="sugg" role="group" aria-label="Starter questions">
            {SUGGESTIONS.map((s) => (
              <SuggestionButton key={s} text={s} />
            ))}
          </div>
        </CopilotThread>

        <div className="cp-drawer-foot">
          <CopilotComposer placeholder="Ask about any client, fund or portfolio…" />
        </div>
      </aside>
    </>
  );
}

/** One starter: asks it as a typed question. */
function SuggestionButton({ text }) {
  const { ask } = useCopilot();
  return (
    <button type="button" onClick={() => callSafely(ask, text, { via: 'text' })}>
      <span aria-hidden="true">→ </span>{text}
    </button>
  );
}
